import { Component, DestroyRef, Inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { IVisualizer, VISUALIZER } from '@jax-data-science/sci-image-visualizer';

import { MainFacade } from '../../../main.facade';
import { RuleService } from '../../../../services/rule.service';
import { RuleInfo, RuleRequest } from '../../../models/rule-config';
import { Request, ProcessType } from '../../../models/request';
import { RawFileInfo, FileProject } from '../../../models/file-info';

/** Mirrors `RulePollService.MIN_POLL_INTERVAL_SECONDS` (jit-parent) - the
 *  server rejects anything lower, so the form validates against it too rather
 *  than letting the user submit and find out from a 400. */
export const MIN_POLL_INTERVAL_SECONDS = 30;

/** A resolved `StorageKey` (see request.ts) - what every tool `Request`'s
 *  input/output fields turn into once a builder has run, baking in the
 *  bucket/path of whichever file happened to be selected. */
function isStorageKeyLike(value: unknown): boolean {
  return !!value && typeof value === 'object' && !Array.isArray(value)
    && Object.keys(value).every((key) => key === 'endpoint' || key === 'bucket' || key === 'object');
}

/**
 * Strips a built tool `Request`'s file/location fields, keeping only its
 * plain parameters, so the persisted template is safe to replay against a
 * different file on every trigger. Every Request subtype's own input/output
 * fields (`align`/`reference`/`shg`, `inputPaths`, `images`, `outputPath`,
 * `outputDir`, `uploadDir`, ...) are `StorageKey`/`StorageKey[]`-shaped
 * (request.ts) with the bucket/path of the file selected when this request
 * was built - persisting them verbatim would keep every future trigger
 * re-processing/overwriting that same original file/location instead of the
 * newly discovered one. Detecting the `StorageKey` shape structurally (rather
 * than listing field names) covers every current and future tool without a
 * per-tool field list; the Rule's own `outputBucket`/`outputPathPrefix`/
 * `folderNamePattern` (RuleFields) are what determine per-trigger output.
 */
export function stripFileReferences(
  request: Record<string, any>,
  preserveKeys: string[] = ['scriptKey'],
): Record<string, any> {
  const template: Record<string, any> = {};
  for (const [key, value] of Object.entries(request)) {
    // `preserveKeys` are StorageKey-shaped but name a reusable resource (e.g. a QuPath script
    // chosen from storage), not a per-image location, so the Rule must keep them.
    if (!preserveKeys.includes(key)) {
      if (isStorageKeyLike(value)) continue;
      if (Array.isArray(value) && value.length > 0 && value.every(isStorageKeyLike)) continue;
    }
    template[key] = value;
  }
  return template;
}

export interface CreateRuleDialogData {
  /** Create mode only: the folder to watch (or its parent, if a file was
   *  selected - see ProcessComponentBase.createRule()). Ignored in edit mode. */
  folder?: RawFileInfo;
  /** Create mode only: the tool's already-built request, exactly as
   *  `submit()` would have sent it - `processType` is read off it, then
   *  stripped for the wire body (see `save()`), matching
   *  `MainFacade.hackRequest`. Ignored in edit mode. */
  request?: Request;
  /** Edit mode only: the Rule being edited (from the Rules tab -
   *  RulesComponent.editSelected()). Its presence switches the whole dialog
   *  into edit mode: every field seeds from it instead of `folder`/`request`,
   *  and `save()` calls `RuleService.update()` instead of `create()`. Its
   *  `processType`/`requestTemplate`/`regions` are carried through
   *  unchanged - this dialog only edits the Rule-level watch/output/schedule
   *  settings, not the tool's own analysis parameters or drawn regions. */
  existingRule?: RuleInfo;
}

/**
 * Dialog for creating OR editing a Rule (bucket-monitoring auto-ingest
 * config) - see doc/JIT-Rules.md Frontend Design and jit-ui#133/#137.
 *
 * Create mode (`data.request` set, from a tool page): uses the current
 * tool's form values as the template applied to every newly discovered
 * file. `data.folder` only seeds the initial "Monitored bucket"/"Monitored
 * folder path" form values; the user can change either before creating.
 *
 * Edit mode (`data.existingRule` set, from the Rules tab): seeds every field
 * from the existing Rule and calls `RuleService.update()` on save instead of
 * `create()`. The tool's analysis parameters and any drawn regions are
 * carried through unchanged, exactly as stored - this dialog has no UI to
 * edit either (regions need the tool's own live plot; params need the
 * tool's own dynamic form), so re-sending them unedited is the only correct
 * choice rather than reconstructing/guessing at them.
 *
 * Opened imperatively via DialogService.open() rather than as a template
 * dialog, mirroring DirectorySubmitDialogComponent - ProcessComponentBase,
 * the shared entry point for every tool, is a template-less directive.
 */
@Component({
  selector: 'app-create-rule-dialog',
  templateUrl: './create-rule-dialog.component.html',
  styleUrls: ['./create-rule-dialog.component.scss'],
})
export class CreateRuleDialogComponent implements OnInit {

  readonly minPollIntervalSeconds = MIN_POLL_INTERVAL_SECONDS;

  form: FormGroup;
  submitting = false;
  projects: FileProject[] = [];
  regionCount = 0;

  constructor(
    private config: DynamicDialogConfig,
    private ref: DynamicDialogRef,
    private fb: FormBuilder,
    private ruleService: RuleService,
    private mainFacade: MainFacade,
    private messageService: MessageService,
    private destroyRef: DestroyRef,
    @Inject(VISUALIZER) private plotService: IVisualizer,
  ) {
    const existing = this.data.existingRule;
    this.form = this.fb.nonNullable.group({
      sourceBucket: [existing?.bucket ?? this.data.folder?.project.bucket, Validators.required],
      sourcePathPrefix: [existing?.pathPrefix ?? this.data.folder?.relPath ?? ''],
      filenameRegex: [existing?.filenameRegex ?? ''],
      recursive: [existing?.recursive ?? false],
      // Empty string = "same bucket as source", matching RuleRequest.outputBucket
      // being optional server-side (RuleCommands/RuleController default to the
      // input bucket when it's absent).
      outputBucket: [existing?.outputBucket ?? ''],
      outputPathPrefix: [existing?.outputPathPrefix ?? ''],
      // Defaults to a per-day results folder so a fresh Rule doesn't dump
      // every triggered run into one flat directory.
      folderNamePattern: [existing?.folderNamePattern ?? '{yyyy-MM-dd}'],
      pollIntervalSeconds: [existing?.pollIntervalSeconds ?? 60,
        [Validators.required, Validators.min(MIN_POLL_INTERVAL_SECONDS)]],
    });
  }

  get data(): CreateRuleDialogData {
    return this.config.data;
  }

  ngOnInit(): void {
    // getProjects$() is a root BehaviorSubject that never completes - without
    // this, every open/close of this short-lived dynamic dialog would leak a
    // subscription (and this component instance) on the facade.
    this.mainFacade.getProjects$().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((projects) => {
      this.projects = projects;
    });
    const existing = this.data.existingRule;
    if (existing) {
      // Read-only summary of what's already stored - this dialog has no live
      // plot to draw/redraw regions against for a Rule opened from the Rules
      // tab, so regions are pass-through only (see save()).
      this.regionCount = existing.regions?.features?.length ?? 0;
      return;
    }
    // Best-effort: not every tool page has drawn regions, and some viewer
    // states don't have a mounted plot yet - either way, no regions is a
    // valid, common case for a fresh Rule.
    try {
      this.regionCount = this.plotService.getRegions()?.length ?? 0;
    } catch {
      this.regionCount = 0;
    }
  }

  get filenameRegexValid(): boolean {
    const value = this.form.value.filenameRegex;
    if (!value) return true;
    try {
      // eslint-disable-next-line no-new
      new RegExp(value);
      return true;
    } catch {
      return false;
    }
  }

  cancel(): void {
    this.ref.close(null);
  }

  get isEdit(): boolean {
    return !!this.data.existingRule;
  }

  save(): void {
    if (this.form.invalid || !this.filenameRegexValid || this.submitting) return;

    this.submitting = true;
    const existing = this.data.existingRule;

    let processType: ProcessType;
    let requestTemplate: Record<string, any>;
    if (existing) {
      // Carried through unchanged - this dialog has no UI to edit the tool's
      // own dynamic parameters, only the Rule-level watch/output/schedule
      // settings below.
      processType = existing.processType as ProcessType;
      requestTemplate = existing.requestTemplate ?? {};
    } else {
      // The bare tool request's parameters only, not processType, a wrapping
      // instruction, or the resolved file/output locations baked in for
      // whichever file was selected when this request was built (see
      // stripFileReferences) - the file differs on every trigger, so the
      // server builds a fresh instruction per discovered file and slots this
      // template into it (doc/JIT-Rules.md Backend Design §4).
      processType = this.data.request!.processType as ProcessType;
      requestTemplate = stripFileReferences(this.data.request as any);
      delete requestTemplate.processType;
    }

    const value = this.form.value;
    const body: RuleRequest = {
      bucket: value.sourceBucket,
      pathPrefix: value.sourcePathPrefix || undefined,
      filenameRegex: value.filenameRegex || undefined,
      recursive: !!value.recursive,
      processType,
      outputBucket: value.outputBucket || undefined,
      outputPathPrefix: value.outputPathPrefix || undefined,
      folderNamePattern: value.folderNamePattern || undefined,
      requestTemplate,
      pollIntervalSeconds: value.pollIntervalSeconds,
      // Editing must not silently re-enable/disable a Rule - that's the
      // Rules tab toolbar's job (activate/deactivate), not this dialog's.
      enabled: existing ? existing.enabled : true,
    };

    if (existing) {
      body.regions = existing.regions; // pass through unchanged - not editable here
    } else if (this.regionCount > 0) {
      try {
        const geoJson = this.plotService.getGeoJsonString(this.plotService.getRegions());
        body.regions = JSON.parse(geoJson);
      } catch {
        // Leave regions unset rather than fail the whole submission over a
        // display-only summary count disagreeing with serialization.
      }
    }

    const saved$ = existing ? this.ruleService.update(existing.id, body) : this.ruleService.create(body);

    saved$.subscribe({
      next: (saved) => {
        this.submitting = false;
        this.messageService.add({
          key: 'app-toast', severity: 'success', summary: existing ? 'Rule Updated' : 'Rule Created',
          detail: `Watching ${saved.bucket}${saved.pathPrefix ? '/' + saved.pathPrefix : ''}`,
        });
        this.ref.close(saved);
      },
      error: (err) => {
        this.submitting = false;
        const detail = err?.status === 403
          ? `You do not have permission to ${existing ? 'update' : 'create'} a rule for this bucket.`
          : (err?.error?.message ?? `Could not ${existing ? 'update' : 'create'} the rule.`);
        this.messageService.add({
          key: 'app-toast', severity: 'error', summary: existing ? 'Update Rule Failed' : 'Create Rule Failed', detail,
        });
      },
    });
  }
}
